'use client';

import { createElement, type ReactNode } from 'react';
import type {
  HITLActionRequest,
  HITLDecision,
  HITLRequestConfirmationDecision,
  ToolCallGenericMessage,
} from '@terra/shared-dto-chat-types';
import { getToolConfig } from '../tool-config';
import { readEditedActionArgs } from './hitl-card.utils';
import type { OnOpenChatObject } from '../hitl-types';
import type { PendingApprovalControls } from '../tool-config-types';
import type { useBackgroundExecution } from '../BackgroundExecutionContext';

type ResolvedDecisionType =
  NonNullable<HITLRequestConfirmationDecision>['type'];

type GetBackgroundExecutionByToolCallId = NonNullable<
  ReturnType<typeof useBackgroundExecution>
>['getBackgroundExecutionByToolCallId'];

export const renderInlineToolResultContent = ({
  actionRequest,
  hitlDecisionType,
  decision,
  toolResultsById,
  getBackgroundExecutionByToolCallId,
  onOpenObject,
}: {
  actionRequest: HITLActionRequest;
  hitlDecisionType: ResolvedDecisionType;
  decision?: HITLDecision;
  toolResultsById?: Record<string, ToolCallGenericMessage>;
  getBackgroundExecutionByToolCallId?: GetBackgroundExecutionByToolCallId;
  onOpenObject?: OnOpenChatObject;
}): ReactNode | undefined => {
  const actionId = actionRequest.toolCallId ?? actionRequest.id;
  const catalogToolResult = actionId ? toolResultsById?.[actionId] : undefined;
  const toolResult =
    catalogToolResult?.status === 'aborted' ? undefined : catalogToolResult;
  const pendingExecution = actionId
    ? getBackgroundExecutionByToolCallId?.(actionId)
    : undefined;
  const pendingActionExecution =
    !toolResult && actionId ? pendingExecution : undefined;

  if (!toolResult && !pendingActionExecution) return undefined;
  const actionConfig = getToolConfig(actionRequest.name);
  const actionIcon = createElement(actionConfig.icon, { size: 12 });
  const resolvedOutput =
    toolResult?.output ??
    (pendingActionExecution
      ? {
          neoThreadId: pendingActionExecution.threadId,
          awaitingCompletion: true as const,
        }
      : undefined);
  const resolvedStatus = toolResult?.status ?? 'in-progress';

  if (actionConfig.renderCard) {
    const renderedCard = actionConfig.renderCard({
      toolCallData: {
        input: readEditedActionArgs(decision) ?? actionRequest.args,
        output: resolvedOutput,
        status: resolvedStatus,
        error: toolResult?.error,
        toolCallId: toolResult?.toolCallId ?? actionId,
        id: toolResult?.id ?? actionId,
        timing: toolResult?.timing,
      },
      toolIcon: actionIcon,
      toolName: actionConfig.name,
      labelPresent: actionConfig.name,
      labelPast: actionConfig.name,
      hitlDecisionType,
      onOpenObject,
      getBackgroundExecutionByToolCallId,
    });

    if (renderedCard !== undefined) {
      return renderedCard;
    }
  }

  if (actionConfig.renderOutput && resolvedOutput !== undefined) {
    return actionConfig.renderOutput({
      content: resolvedOutput,
      onOpenObject,
    });
  }

  return undefined;
};

export const renderInlineActionRequestContent = ({
  actionRequest,
  hitlDecisionType,
  decision,
  headerActions,
  pendingApproval,
  onOpenReview,
  onOpenObject,
  getBackgroundExecutionByToolCallId,
}: {
  actionRequest: HITLActionRequest;
  hitlDecisionType?: ResolvedDecisionType;
  decision?: HITLDecision;
  headerActions?: ReactNode;
  pendingApproval?: PendingApprovalControls;
  onOpenReview?: () => void;
  onOpenObject?: OnOpenChatObject;
  getBackgroundExecutionByToolCallId?: GetBackgroundExecutionByToolCallId;
}): ReactNode | undefined => {
  const actionConfig = getToolConfig(actionRequest.name);

  if (!actionConfig.renderCard) {
    return undefined;
  }

  const actionIcon = createElement(actionConfig.icon, { size: 12 });

  return actionConfig.renderCard({
    toolCallData: {
      input: readEditedActionArgs(decision) ?? actionRequest.args,
      output: undefined,
      status: 'started',
      toolCallId: actionRequest.toolCallId,
      id: actionRequest.id,
    },
    toolIcon: actionIcon,
    toolName: actionConfig.name,
    labelPresent: actionConfig.name,
    labelPast: actionConfig.name,
    hitlDecisionType,
    headerActions,
    pendingApproval,
    onOpenObject,
    onOpenReview,
    getBackgroundExecutionByToolCallId,
  });
};
